/**
 * /tracking/android-sdk — Android SDK config page (feature #184).
 *
 * Declares a TanStack Router route and renders the `AndroidSdkPage` feature.
 * The component is picked up by the root-owned `router.tsx` module
 * augmentation during the wire-up cycle — agents never touch `router.tsx`.
 *
 * `?appId=` is reserved so operators can deep-link into a specific Android
 * app's config + installations pane from elsewhere in the dashboard.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { AndroidSdkPage } from "@/features/android-sdk/AndroidSdkPage";

interface TrackingAndroidSdkSearch {
	appId?: string;
}

function isValidAppIdQuery(v: unknown): v is string {
	return typeof v === "string" && /^[A-Za-z0-9_.:-]{1,128}$/.test(v);
}

export const trackingAndroidSdkRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/tracking/android-sdk",
	validateSearch: (raw: Record<string, unknown>): TrackingAndroidSdkSearch => {
		const v = raw.appId;
		if (isValidAppIdQuery(v)) {
			return { appId: v };
		}
		return {};
	},
	component: TrackingAndroidSdkRoute,
});

export default trackingAndroidSdkRoute;

// ---------------------------------------------------------------------------
// Page shell — breadcrumb + live feature component.
// ---------------------------------------------------------------------------

export function TrackingAndroidSdkRoute() {
	const { appId } = trackingAndroidSdkRoute.useSearch();
	return (
		<div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-6 pt-6 sm:pt-10">
			<Breadcrumb />
			<AndroidSdkPage initialAppId={appId} />
		</div>
	);
}

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Tracking</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Android SDK</li>
			</ol>
		</nav>
	);
}

// Named re-export for the root wire-up cycle.
export { TrackingAndroidSdkRoute as TrackingAndroidSdkPage };
